import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, Loader2 } from "lucide-react";
import { fmt, fmtEur, computeLocal } from "./investmentUtils";

export const InvestmentFormDialog = ({ form, setForm, onSave, onClose, saving, title }) => {
  const calc = useMemo(() => computeLocal(form), [form]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isBarter = form.deal_type === "barter" || form.deal_type === "mixed";

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle data-testid="investment-dialog-title">{title}</DialogTitle>
        <DialogDescription>Fill in the details to calculate investment returns</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
        <div className="space-y-1">
          <Label>Name *</Label>
          <Input data-testid="investment-name" value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Seaside Plot Opportunity" />
        </div>
        <div className="space-y-1">
          <Label>Location</Label>
          <Input data-testid="investment-location" value={form.location} onChange={e => set("location", e.target.value)} placeholder="e.g. Coastal District" />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label>Description</Label>
          <Textarea data-testid="investment-description" value={form.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="Brief description of the opportunity..." />
        </div>

        {/* RESIDENTIAL AREA */}
        <div className="md:col-span-2 border-t pt-3">
          <p className="text-sm font-semibold text-slate-500 mb-2">BUILDING AREA</p>
        </div>
        <div className="space-y-1">
          <Label>Total Land Area (m²)</Label>
          <Input data-testid="investment-land-area" type="number" value={form.total_land_area_m2} onChange={e => set("total_land_area_m2", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Total Gross Buildable Area (m²)</Label>
          <Input data-testid="investment-gross-m2" type="number" value={form.gross_buildable_m2} onChange={e => set("gross_buildable_m2", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Residential Deduction %</Label>
          <Input data-testid="investment-deduction" type="number" value={form.deduction_percentage} onChange={e => set("deduction_percentage", e.target.value)} />
        </div>
        <div className="space-y-1 flex items-end">
          <div className="p-3 rounded-sm bg-slate-50 w-full text-center">
            <span className="text-xs text-slate-500 block">Residential Gross: {fmt(calc.resGross)} m²</span>
            <span className="text-lg font-bold text-slate-800" data-testid="calc-res-net">Net {fmt(calc.resNet)} m²</span>
          </div>
        </div>

        {/* COMMERCIAL AREA */}
        <div className="space-y-1">
          <Label>Commercial Gross Area (m²)</Label>
          <Input data-testid="investment-comm-gross" type="number" value={form.commercial_gross_m2} onChange={e => set("commercial_gross_m2", e.target.value)} placeholder="0" />
        </div>
        <div className="space-y-1">
          <Label>Commercial Deduction %</Label>
          <Input data-testid="investment-comm-ded" type="number" value={form.commercial_deduction_percentage} onChange={e => set("commercial_deduction_percentage", e.target.value)} placeholder="20" />
        </div>

        {calc.totalNet > 0 && (
          <div className="md:col-span-2 p-3 rounded-sm bg-blue-50/60 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div><span className="text-xs text-blue-500 block">Res. Gross</span><span className="text-sm font-bold text-blue-700">{fmt(calc.resGross)} m²</span></div>
            <div><span className="text-xs text-blue-500 block">Res. Net</span><span className="text-sm font-bold text-blue-700">{fmt(calc.resNet)} m²</span></div>
            <div><span className="text-xs text-blue-500 block">Comm. Net</span><span className="text-sm font-bold text-blue-700">{fmt(calc.commNet)} m²</span></div>
            <div><span className="text-xs text-blue-500 block">Total Net</span><span className="text-sm font-bold text-blue-800">{fmt(calc.totalNet)} m²</span></div>
          </div>
        )}

        {/* DEAL STRUCTURE */}
        <div className="md:col-span-2 border-t pt-3">
          <p className="text-sm font-semibold text-slate-500 mb-2">DEAL STRUCTURE</p>
        </div>
        <div className="space-y-1">
          <Label>Deal Type</Label>
          <Select value={form.deal_type} onValueChange={v => set("deal_type", v)}>
            <SelectTrigger data-testid="investment-deal-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash Purchase</SelectItem>
              <SelectItem value="barter">Barter (% of Apartments)</SelectItem>
              <SelectItem value="mixed">Mixed (Cash + %)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.deal_type !== "barter" && (
          <div className="space-y-1">
            <Label>Land Price (€)</Label>
            <Input data-testid="investment-land-price" type="number" value={form.land_price} onChange={e => set("land_price", e.target.value)} />
          </div>
        )}
        {isBarter && (
          <div className="space-y-1">
            <Label>Owner's % of Built Area</Label>
            <Input data-testid="investment-owner-pct" type="number" value={form.owner_percentage} onChange={e => set("owner_percentage", e.target.value)} placeholder="e.g. 30" />
          </div>
        )}
        {form.deal_type === "mixed" && (
          <div className="space-y-1">
            <Label>Cash Portion (€)</Label>
            <Input data-testid="investment-cash-portion" type="number" value={form.cash_portion} onChange={e => set("cash_portion", e.target.value)} />
          </div>
        )}
        {isBarter && calc.ownerTotal > 0 && (
          <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="p-2.5 rounded-sm bg-orange-50 text-center">
              <span className="text-[11px] text-orange-500 block">Owner Residential</span>
              <span className="text-sm font-bold text-orange-700">{fmt(calc.ownerResM2)} m²</span>
            </div>
            <div className="p-2.5 rounded-sm bg-orange-50 text-center">
              <span className="text-[11px] text-orange-500 block">Owner Commercial</span>
              <span className="text-sm font-bold text-orange-700">{fmt(calc.ownerCommM2)} m²</span>
            </div>
            <div className="p-2.5 rounded-sm bg-emerald-50 text-center">
              <span className="text-[11px] text-emerald-500 block">You Residential</span>
              <span className="text-sm font-bold text-emerald-700">{fmt(calc.yourRes)} m²</span>
            </div>
            <div className="p-2.5 rounded-sm bg-emerald-50 text-center">
              <span className="text-[11px] text-emerald-500 block">You Commercial</span>
              <span className="text-sm font-bold text-emerald-700">{fmt(calc.yourComm)} m²</span>
            </div>
          </div>
        )}

        {/* COSTS & REVENUE */}
        <div className="md:col-span-2 border-t pt-3">
          <p className="text-sm font-semibold text-slate-500 mb-2">COSTS & REVENUE</p>
        </div>
        <div className="space-y-1">
          <Label>Communal Tax per m² (€)</Label>
          <Input data-testid="investment-tax" type="number" value={form.communal_tax_per_m2} onChange={e => set("communal_tax_per_m2", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Construction Cost per m² (€)</Label>
          <Input data-testid="investment-construction" type="number" value={form.construction_cost_per_m2} onChange={e => set("construction_cost_per_m2", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Residential Sale Price / m² (€)</Label>
          <Input data-testid="investment-res-price" type="number" value={form.residential_sale_price_per_m2} onChange={e => set("residential_sale_price_per_m2", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Commercial Sale Price / m² (€)</Label>
          <Input data-testid="investment-comm-price" type="number" value={form.commercial_sale_price_per_m2} onChange={e => set("commercial_sale_price_per_m2", e.target.value)} />
        </div>

        {/* BARTER CASH OFFER CALCULATOR */}
        {isBarter && calc.barterTotal > 0 && (
          <div className="md:col-span-2 p-4 rounded-sm bg-amber-50/80 border border-amber-200" data-testid="cash-offer-calculator">
            <p className="text-sm font-semibold text-amber-700 flex items-center gap-1.5 mb-2">
              <DollarSign className="w-4 h-4" />Barter Value Calculator
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <div className="p-2.5 rounded-sm bg-white border border-amber-100 text-center">
                <span className="text-[11px] text-amber-500 block">Residential ({fmt(calc.ownerResM2)} m²)</span>
                <span className="text-sm font-bold text-amber-800">{fmtEur(calc.barterResValue)}</span>
              </div>
              {calc.barterCommValue > 0 && (
                <div className="p-2.5 rounded-sm bg-white border border-amber-100 text-center">
                  <span className="text-[11px] text-amber-500 block">Commercial ({fmt(calc.ownerCommM2)} m²)</span>
                  <span className="text-sm font-bold text-amber-800">{fmtEur(calc.barterCommValue)}</span>
                </div>
              )}
              <div className="p-2.5 rounded-sm bg-amber-100 border border-amber-200 text-center">
                <span className="text-[11px] text-amber-600 block">Total Barter Value</span>
                <span className="text-sm font-bold text-amber-900">{fmtEur(calc.barterTotal)}</span>
              </div>
            </div>
            <p className="text-xs text-amber-600/80 mb-2">Alternative barter scenarios — click to apply:</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {calc.barterScenarios.map(s => (
                <button key={s.pct} type="button"
                  onClick={() => set("owner_percentage", String(s.pct))}
                  className={`p-2 rounded-sm border text-center transition-all hover:scale-[1.03] cursor-pointer ${
                    Math.round(parseFloat(form.owner_percentage) || 0) === s.pct
                      ? "bg-amber-200 border-amber-400" : "bg-white border-amber-100 hover:border-amber-300"
                  }`} data-testid={`barter-scenario-${s.pct}`}>
                  <span className="text-[11px] text-amber-600 font-semibold block">{s.pct}%</span>
                  <span className="text-[10px] text-slate-500 block">{fmt(s.ownerRes)} res</span>
                  <span className="text-[10px] text-slate-500 block">{fmt(s.ownerComm)} comm</span>
                  <span className="text-xs font-bold text-amber-800 block mt-0.5">{fmtEur(s.cashEquiv)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={v => set("status", v)}>
            <SelectTrigger data-testid="investment-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Notes</Label>
          <Textarea data-testid="investment-notes" value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} />
        </div>

        {/* LIVE CALCULATION */}
        <div className="md:col-span-2 p-4 rounded-sm bg-gradient-to-r from-slate-50 to-blue-50 border">
          <p className="text-sm font-semibold text-slate-600 mb-3">LIVE CALCULATION</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-sm">
            <div><span className="text-slate-500 block">Total Costs</span><span className="font-bold text-slate-800">{fmtEur(calc.totalCosts)}</span></div>
            <div><span className="text-slate-500 block">Total Revenue</span><span className="font-bold text-slate-800">{fmtEur(calc.totalRevenue)}</span></div>
            <div><span className="text-slate-500 block">Profit</span><span className={`font-bold ${calc.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtEur(calc.profit)}</span></div>
            <div><span className="text-slate-500 block">Margin</span><span className={`font-bold ${calc.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>{calc.margin.toFixed(1)}%</span></div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="investment-cancel-btn">Cancel</Button>
        <Button onClick={onSave} disabled={saving || !form.name} data-testid="investment-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Save"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
